/**
 * Numéro 8 du programme pédagogique ETM — L'environnement
 *
 * Thème ETM officiel : Environnement (code thème E).
 * Couvre : pollution moto, éco-conduite, ZFE / Crit'Air, bruit moteur, conditions météo,
 * partage de la route avec la nature et la biodiversité.
 */

import { ShieldCheck, AlertTriangle, Lightbulb, Quote, BookOpen, Users } from 'lucide-react';

export const environnementEtmIssue = {
  // META
  slug: 'environnement',
  themeCode: 'E',
  issueNumber: 8,
  totalIssues: 9,
  category: "La moto responsable",
  shortTitle: "L'environnement",
  title: 'Rouler propre, rouler malin',
  kicker: 'Numéro 8 du programme pédagogique ETM',
  subtitle:
    "Éco-conduite, bruit, pollution, ZFE, Crit'Air, conditions météo. La moto responsable du XXIe siècle : tout ce que tu dois savoir pour rester légal en ville et minimiser ton empreinte.",
  heroImage: '/img/magazines/environnement.jpg',
  heroImageAlt: 'Deux motards en virage sur une route sinueuse bordée d’arbres — illustration de la moto en pleine nature',
  readTimeMin: 12,

  // SEO
  seoDescription:
    "Rouler responsable à moto pour l'ETM 2026 : ZFE Crit'Air, éco-conduite, pollution sonore, conditions météo, biodiversité. Cours + QCM auto-évaluant 100 % gratuit.",
  seoKeywords:
    "éco-conduite moto, ZFE Crit'Air moto, pollution sonore moto, météo conduite moto, ETM environnement",

  // ÉDITO
  edito: {
    title: "La moto n'est pas écologique — mais elle peut l'être",
    body: "Une moto émet <strong>environ 60 % moins de CO2 par km</strong> qu'une voiture moyenne (sur sa durée de vie). Mais elle pollue davantage en <strong>NOx, hydrocarbures et particules fines</strong>, et son bruit cause des nuisances majeures en ville. Bonne nouvelle : <strong>tu peux réduire de 30 % ta pollution</strong> juste avec ta manière de rouler. Ce numéro te donne les réflexes éco-conduite, les règles des ZFE, et les bons gestes pour cohabiter avec ton environnement.",
    signedBy: 'La rédaction Code 2 Roues',
  },

  // SECTIONS
  sections: [
    // === CHAPITRE 1 — ZFE / Crit'Air ===
    {
      type: 'lesson',
      icon: ShieldCheck,
      partLabel: 'Chapitre 1',
      title: "ZFE & Crit'Air : ce qui change pour les motos en 2026",
      paragraphs: [
        "Les <strong>Zones à Faibles Émissions Mobilité (ZFE-m)</strong> se multiplient en France. En 2026, plus de <strong>40 métropoles</strong> en ont déployé une, dont Paris, Lyon, Marseille, Toulouse, Strasbourg, Rouen, Grenoble, Reims.",
        "<strong>Le système Crit'Air pour motos</strong> classe ton 2-roues selon sa norme Euro :",
        "<strong>Crit'Air 0 (vert)</strong> : motos électriques ou hydrogène. Accès libre partout. <strong>Crit'Air 1 (violet)</strong> : motos Euro 4 et 5 (immatriculées après 2017). Accès ZFE autorisé. <strong>Crit'Air 2 (jaune)</strong> : motos Euro 3 (entre 2007 et 2016). Restrictions partielles. <strong>Crit'Air 3 (orange)</strong> : motos Euro 2 (2004-2006). <strong>Crit'Air 4 (bordeaux)</strong> : motos Euro 1 (2000-2003). <strong>Non classé </strong> : motos avant 2000. Souvent interdites en ZFE.",
        "<strong>Sanction en cas de circulation hors-droits en ZFE</strong> : 68 € (contravention 3ème classe). Le contrôle se fait par radars automatiques (en cours de déploiement 2026) ou par les forces de l'ordre.",
        "<strong>Bon à savoir</strong> : la vignette Crit'Air coûte 3,77 € (commande sur certificat-air.gouv.fr). Elle doit être collée <strong>sur la fourche</strong> de la moto (zone visible de l'avant). Sanction si absence en ZFE : 68 € de plus.",
      ],
      keyPoints: [
        "40+ métropoles françaises en ZFE en 2026",
        "Vignette Crit'Air à acheter sur certificat-air.gouv.fr (3,77 €)",
        "Coller sur la fourche, visible de l'avant",
        "Hors-droits ZFE = 68 € d'amende",
      ],
    },

    // === SAVIEZ-VOUS ===
    {
      type: 'didyouknow',
      icon: Lightbulb,
      title: 'Le saviez-vous ?',
      body: "Une moto Euro 5 émet environ <strong>2 g de CO/km</strong>, contre <strong>14 g/km</strong> pour une moto Euro 2. La norme Euro 5+ (en vigueur depuis 2024) impose en plus un suivi OBD (On Board Diagnostic) renforcé sur les missions polluantes. Conséquence : rouler en moto récente ne pollue pas seulement moins en CO2 — ça divise par 7 les émissions urbaines des polluants les plus nocifs pour les piétons et cyclistes que tu croises.",
    },

    // === CHAPITRE 2 — Éco-conduite ===
    {
      type: 'lesson',
      icon: ShieldCheck,
      partLabel: 'Chapitre 2',
      title: "L'éco-conduite moto : -30 % de carburant en 7 réflexes",
      paragraphs: [
        "L'éco-conduite n'est pas réservée aux voitures. Selon l'ADEME, un motard peut réduire sa consommation de <strong>20 à 30 %</strong> en appliquant 7 réflexes pros :",
        "<strong>1. Anticipe</strong>. Garde 2 secondes d'avance sur la circulation : tu réduis le couple freinage/réaccélération qui coûte le plus de carburant.",
        "<strong>2. Monte les rapports tôt</strong>. La plupart des motos sont optimisées entre 3 000 et 5 000 tr/min. Au-delà, la consommation grimpe en exponentielle.",
        "<strong>3. Coupe ton moteur au-delà de 30 secondes d'arrêt</strong>. Embouteillage long, feu rouge interminable : moteur éteint. C'est aussi moins de bruit et moins de chaleur.",
        "<strong>4. Pression des pneus optimale</strong>. Un pneu sous-gonflé de 20 % = +5 % de consommation. Contrôle mensuel obligatoire.",
        "<strong>5. Évite les charges inutiles</strong>. Une top-case vide pesant 4 kg = +2 % de conso sur l'année. À enlever si pas utilisée.",
        "<strong>6. Plage de vitesse optimale</strong>. Une moto consomme le moins entre 60 et 90 km/h. Au-delà de 110 km/h, la résistance à l'air explose (force au carré de la vitesse).",
        "<strong>7. Entretien rigoureux</strong>. Chaîne mal lubrifiée, filtre à air encrassé, bougies usées : -10 à -15 % de rendement. Suis le calendrier constructeur à la lettre.",
      ],
      keyPoints: [
        'Anticipation = le levier n°1 d’éco-conduite',
        'Moteur coupé dès 30 sec d’arrêt',
        'Pneus bien gonflés = -5 % de conso',
        "60-90 km/h = plage de conso optimale",
      ],
    },

    // === PIÈGE ===
    {
      type: 'trap',
      icon: AlertTriangle,
      title: 'Le piège à éviter',
      body: "« Une moto trafiquée (pot d'échappement modifié) fait plus de bruit donc je suis plus visible, donc plus en sécurité. » <strong>FAUX et illégal.</strong> Le bruit d'une moto NE change PAS sa visibilité (l'œil prime sur l'oreille en situation de trafic). En revanche, le pot non homologué expose à : amende 135 € + immobilisation + obligation de remettre la pièce d'origine. Et depuis 2023, les <strong>radars sonores</strong> (Hydre / Methys) flashent automatiquement les motos dépassant 90 dB(A). PV de 135 € envoyé sans contact humain.",
    },

    // === CHAPITRE 3 — Météo ===
    {
      type: 'lesson',
      icon: ShieldCheck,
      partLabel: 'Chapitre 3',
      title: 'Conditions météo : 4 scénarios qui changent tout',
      paragraphs: [
        "Ta moto, c'est l'aéronef du quotidien : tu es exposé à tout. Voici les 4 scénarios météo qui te demandent une adaptation immédiate :",
        "<strong>1. Pluie</strong>. Les 10 premières minutes sont les plus dangereuses : l'eau remonte les hydrocarbures et la poussière du bitume, formant un film glissant. Réflexes : <strong>réduis la vitesse de 20 à 30 %</strong>, augmente à 4 secondes la distance de sécurité, évite les marquages au sol (peinture = patinoire), freine en douceur des 2 roues.",
        "<strong>2. Brouillard</strong>. Visibilité < 50 m = feux antibrouillard ALLUMéS obligatoires (avant + arrière). Vitesse adaptée : tu dois pouvoir t'arrêter dans le champ éclairé. Sanction si feux non allumés : 135 €.",
        "<strong>3. Vent fort (>50 km/h)</strong>. Particulièrement dangereux sur les ponts et viaducs (effet Venturi). Réflexes : tiens le guidon ferme avec les coudes légèrement pliés, anticipe les <strong>rafales latérales</strong> en serrant les genoux contre le réservoir, réduis la vitesse de 30 % sur ouvrages d'art.",
        "<strong>4. Verglas / neige / gel</strong>. Évite simplement la moto. Même avec des pneus M+S (Mud and Snow), une moto perd <strong>80 % de son adhérence</strong> sur le verglas. Pas de comparaison possible avec une voiture en sécurité active. Si tu DOIS rouler : 30 km/h max, pieds en appui constant, freinage par décélération moteur uniquement.",
      ],
      keyPoints: [
        'Pluie : 10 premières minutes les plus glissantes',
        "Brouillard < 50m : feux antibrouillards obligatoires",
        'Vent fort : viaducs = zone critique',
        'Verglas : reste chez toi, ou 30 km/h max et frein moteur',
      ],
    },

    // === CONSEIL DU MONITEUR ===
    {
      type: 'tip',
      icon: Quote,
      title: 'Le conseil du moniteur ECSR',
      body: "« Mes élèves me demandent souvent : « Le pneu pluie, ce n'est pas mieux ? » Ma réponse : un <strong>pneu touring polyvalent récent</strong> bien gonflé fait largement le job. L'investissement intelligent, ce n'est pas le pneu spécifique, c'est l'<strong>entretien</strong> : <strong>4 mm de gomme minimum</strong> (la loi accepte 1 mm mais c'est suicidaire sous pluie). Et après 5 ans, même neuf, change-les — le caoutchouc durcit avec le temps. »",
    },

    // === CHAPITRE 4 — Bruit ===
    {
      type: 'lesson',
      icon: ShieldCheck,
      partLabel: 'Chapitre 4',
      title: 'Le bruit moto : ennemi public n°1 en ville',
      paragraphs: [
        "En 2023, l'INSERM a classé le bruit du trafic routier comme <strong>2ème cause de mortalité environnementale en France</strong> (après la pollution de l'air). Les motos sont surreprésentées dans les nuisances sonores urbaines, notamment de nuit.",
        "<strong>Le seuil légal d'une moto homologuée</strong> : <strong>77 dB(A)</strong> au passage statique, mesuré à 50 cm du pot. Au-delà = pot d'échappement non conforme = 135 € + immobilisation.",
        "<strong>Les radars sonores</strong> Hydre et Méthys, déployés depuis 2023, mesurent automatiquement le bruit au passage. Seuil de déclenchement : <strong>90 dB(A)</strong> en ville. PV automatique : 135 €. Paris, Bron, Toulouse, Saint-Lambert-des-Bois, Strasbourg en sont déjà équipés.",
        "<strong>Réflexes anti-bruit</strong> : ne jamais révolutionner inutilement (notamment au démarrage matinal), ne pas faire crisser les pneus, éviter les accélérations en sortie de feu rouge à fond. Le motard pro est aussi le voisin discret.",
      ],
      keyPoints: [
        "Bruit : 77 dB(A) max homologué statique",
        "Radars sonores actifs depuis 2023 (seuil 90 dB)",
        "PV automatique 135 €",
        "Être motard pro = être voisin discret",
      ],
    },

    // === TÉMOIGNAGE ===
    {
      type: 'story',
      icon: Users,
      title: "Le témoignage : « Le pot trafiqué m'a coûté mille euros »",
      body: "« J'avais installé un pot non homologué sur ma 600 SS, j'aimais le son rauque. Pendant 8 mois, aucun problème. Et puis un soir à Paris, en septembre 2024, un radar sonore boulevard de Belleville. PV de 135 € sans le savoir. J'ai reçu 4 PV en un mois — ils ont chacun été déclenchés aux mêmes endroits. Total : 540 € + remise en conformité du pot (320 €) + contrôle mines pour valider la conformité (75 €). 935 € pour un bruit que je trouvais cool. Aujourd'hui, j'ai un pot d'origine, je roule en mode éco et personne ne me regarde de travers. C'était con. » <em>— Antoine, 31 ans</em>",
    },
  ],

  // CHIFFRE
  keyStat: {
    kicker: 'Le chiffre à retenir',
    big: '-30 %',
    middle: 'de consommation',
    label: 'possible avec une éco-conduite moto rigoureuse',
    source: 'ADEME — Étude éco-conduite 2-roues 2024',
    accent: '#15803d',
  },

  // GLOSSAIRE
  glossary: [
    { term: 'ZFE-m', def: "Zone à Faibles Émissions Mobilité : périmètre urbain où la circulation est restreinte selon la catégorie Crit'Air du véhicule." },
    { term: "Crit'Air", def: "Vignette obligatoire en ZFE classant le véhicule selon ses émissions polluantes (0 à 5). Pour motos : collée sur la fourche." },
    { term: 'Euro 5+', def: "Norme antipollution européenne 2024 imposant un OBD renforcé et une réduction des émissions de CO, NOx et HC." },
    { term: 'Éco-conduite', def: "Ensemble de techniques permettant de réduire la consommation de carburant et l'impact environnemental d'un véhicule (20-30 % d'économie)." },
    { term: 'Radar sonore', def: "Dispositif automatique de mesure du bruit au passage (Hydre / Methys), déclenchant un PV au-dessus de 90 dB(A)." },
    { term: 'OBD', def: "On Board Diagnostic : système embarqué surveillant les émissions polluantes et les pannes moteur. Obligatoire en Euro 5+." },
    { term: 'Pneu M+S', def: "Mud and Snow : marquage indiquant une bande de roulement adaptée à la boue et la neige. Inefficace sur le verglas pour une moto." },
    { term: 'Effet Venturi', def: "Accélération du flux d'air dans un goulet : phénomène qui rend les ponts particulièrement venteux." },
    { term: 'Antibrouillard', def: "Feu spécifique (avant et/ou arrière) à utiliser uniquement quand la visibilité est inférieure à 50 m. Sanction si abus : 135 €." },
    { term: 'INSERM', def: "Institut National de la Santé et de la Recherche Médicale. A classé le bruit du trafic comme 2ème cause de mortalité environnementale en France en 2023." },
  ],

  // QCM
  quiz: {
    title: 'Teste ta compréhension',
    description:
      "10 questions sur l'environnement et la moto responsable, tirées de la banque officielle ETM. Tu dois en réussir <strong>au moins 8 sur 10</strong>.",
    themeCode: 'E',
    questionsCount: 10,
    passingScore: 8,
  },

  // NEXT
  nextIssue: {
    slug: 'premiers-secours',
    number: 9,
    title: 'Tu peux sauver une vie en 3 gestes',
    short: 'Premiers secours',
  },
};

export default environnementEtmIssue;
